import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  CellTower,
  Security,
  BatteryChargingFull,
  Wifi,
  CheckCircle,
  ArrowForward,
  Speed,
  Business,
  Router,
  Public,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const TelecomTowers = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const benefits = [
    'Prime locations with optimal coverage',
    'Competitive leasing rates',
    'Flexible contract terms (1-10 years)',
    'Remote monitoring and management',
    'Climate-controlled equipment shelters',
    'Multiple carrier connectivity options',
  ];

  return (
    <Box>
      {/* Hero Section with Image */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/towers.jpg"
          alt="Telecom Towers"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Telecom Towers"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Telecom Tower Infrastructure
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              The backbone of mobile and wireless communication with 100+ towers across 5+ countries
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Benefits & Leasing Section - Centered */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip 
              label="Tower Leasing" 
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
              Colocation & Tower Leasing
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 5, lineHeight: 1.8, maxWidth: 800, mx: 'auto' }}>
              Lease space on our towers and benefit from our premium infrastructure, 
              strategic locations, and comprehensive support services.
            </Typography>

            <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} key={index}>
                  <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                    <CheckCircle sx={{ color: '#e60108' }} />
                    <Typography variant="body1" sx={{ color: '#191919' }}>{benefit}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/contact')}
                sx={{ bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
              >
                Request Tower Space
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      <CTASection 
        title="Expand Your Wireless Network" 
        subtitle="Lease space on our towers in strategic locations"
        buttonText="Check Coverage"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default TelecomTowers;